import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { appUrl } from "@/lib/platform";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, QrCode, Eye, Trash2, ExternalLink, Package } from "lucide-react";
import QrModal from "@/components/QrModal";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [qrProduct, setQrProduct] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get("/products", {
        params: { q, status: statusFilter || undefined },
      });
      if (!cancelled) setProducts(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [q, statusFilter, tick]);

  const del = async (id) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    await api.delete(`/products/${id}`);
    setTick((t) => t + 1);
  };

  const statusBadge = (status) => (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
        status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-heda-cream text-heda-mute"
      }`}
    >
      {status}
    </span>
  );

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="hed-overline">Catalog</div>
          <h1 className="mt-1 font-display text-2xl font-bold text-heda-ink md:text-[30px]">Products</h1>
        </div>
        <Link to="/admin/products/new" className="hed-btn-gold" data-testid="products-new-button">
          <Plus size={14} /> New product
        </Link>
      </div>

      {/* Search + filter row */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
          <input
            data-testid="products-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, code, category, material..."
            className="heda-input bg-white py-2.5 pl-9"
          />
        </div>
        <div className="flex items-center gap-1 rounded-full border border-heda-line bg-white p-1">
          {["", "draft", "published"].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s || "all"}-button`}
              className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition ${
                statusFilter === s ? "bg-heda-ink text-white" : "text-heda-mute hover:text-heda-ink"
              }`}
            >
              {s ? s.toUpperCase() : "ALL"}
            </button>
          ))}
        </div>
      </div>

      <div className="heda-card overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="heda-thead">
            <tr>
              <th className="px-3 py-3 sm:px-4">Product</th>
              <th className="hidden px-4 py-3 md:table-cell">Code</th>
              <th className="hidden px-4 py-3 md:table-cell">Category</th>
              <th className="hidden px-4 py-3 sm:table-cell">Status</th>
              <th className="px-3 py-3 text-right sm:px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-heda-line transition hover:bg-heda-cream/40" data-testid={`product-row-${p.id}`}>
                <td className="px-3 py-2.5 sm:px-4">
                  <button
                    onClick={() => nav(`/admin/products/${p.id}/edit`)}
                    className="flex min-w-0 items-center gap-3 text-left"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-heda-line bg-heda-cream">
                      {p.images?.[0] ? (
                        <img src={fileUrl(p.images[0].storage_path)} className="h-full w-full object-cover" alt="" />
                      ) : (
                        <span className="text-xs text-heda-mute">—</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-heda-ink">{p.name || "Untitled draft"}</div>
                      <div className="text-[11.5px] text-heda-mute">{p.views || 0} views</div>
                      <div className="mt-1 sm:hidden">{statusBadge(p.status)}</div>
                    </div>
                  </button>
                </td>
                <td className="hidden px-4 py-2.5 tabular-nums text-[13px] text-heda-sub md:table-cell">{p.code || "—"}</td>
                <td className="hidden px-4 py-2.5 text-heda-sub md:table-cell">{p.category || "—"}</td>
                <td className="hidden px-4 py-2.5 sm:table-cell">{statusBadge(p.status)}</td>
                <td className="px-3 py-2.5 sm:px-4">
                  <div className="flex items-center justify-end">
                    {p.status === "published" && p.code && (
                      <>
                        <button
                          onClick={() => setQrProduct(p)}
                          className="hed-icon-btn"
                          title="QR code"
                          data-testid={`product-qr-button-${p.id}`}
                        >
                          <QrCode size={16} />
                        </button>
                        <a
                          href={appUrl(`/product/${p.code}`)}
                          target="_blank"
                          rel="noreferrer"
                          className="hed-icon-btn"
                          title="Open public page"
                        >
                          <ExternalLink size={16} />
                        </a>
                      </>
                    )}
                    <button
                      onClick={() => nav(`/admin/products/${p.id}/edit`)}
                      className="hed-icon-btn"
                      title="Edit"
                      data-testid={`product-edit-button-${p.id}`}
                    >
                      <Eye size={16} />
                    </button>
                    {user?.role === "owner" && (
                      <button
                        onClick={() => del(p.id)}
                        className="hed-icon-btn hover:bg-red-50 hover:text-red-700"
                        title="Delete"
                        data-testid={`product-delete-button-${p.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-14 text-center">
                  <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-heda-olive-tint">
                    <Package size={26} className="text-heda-olive" />
                  </div>
                  <p className="text-sm text-heda-mute">
                    No products yet. Click &ldquo;New product&rdquo; to upload your first brochure.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {qrProduct && <QrModal product={qrProduct} onClose={() => setQrProduct(null)} />}
    </AdminLayout>
  );
}
